// eslint-disable-next-line no-unused-vars

import { MonitorPlugin, SavesPlugin } from './components';
import { AppUrlsPlugin } from './components/appurls/AppUrlsPlugin';
import { CaptionsPlugin } from './components/captions/CaptionsPlugin';
import { CopyPlugin } from './components/copy/CopyPlugin';
import { GameHudPlugin } from './components/gamehud/GameHudPlugin';
import { SceneControllerPlugin } from './components/sceneController/SceneControllerPlugin';
import { SettingsPlugin } from './components/settings/SettingsPlugin';
import { ShortcutsPlugin } from './components/shortcuts/ShortcutsPlugin';
import { SoundPlugin } from './components/sound/SoundPlugin';
import { VibratePlugin } from './components/vibrate/VibratePlugin';
import { ViewportPlugin } from './components/viewport/ViewportPlugin';
import { LoaderPlugin } from './components/gameloader';
import { LoggerPlugin } from './components';
import { getDeviceMetric } from './utils';
import * as SpinePlugin from 'phaser/plugins/spine/dist/SpinePlugin';
/*
 * https://github.com/samme/phaser-plugin-game-scale
 * fit — scale the canvas up or down to fit the container, and within min/max lengths (if set).
 * resize — change the game dimensions to fit the container, and within min/max lengths (if set).
 * resize-and-fit — resize within min/max lengths, then scale the canvas to fit any remaining space within the container.
 * none — set the canvas scale to 100%.
 */

const GAME_WIDTH_DEFAULT = 1680;
const GAME_HEIGHT_DEFAULT = 720;
const GAME_WIDTH_SAFE = 960;
const GAME_HEIGHT_SAFE = 720;
const GAME_DIV_ID = 'game-holder';

/**
 * @method getConfigBase
 * @param {GetConfigBaseOptions} options
 * @description Get a base configuration for use with AppBase, comes with predefined settings and customizable options
 *
 */
export const getConfigBase = ({
  type = Phaser.AUTO,
  parent = GAME_DIV_ID,
  width = GAME_WIDTH_DEFAULT,
  height = GAME_HEIGHT_DEFAULT,
  safeWidth = GAME_WIDTH_SAFE,
  safeHeight = GAME_HEIGHT_SAFE,
  physics = true,
  gameURL = './',
  assetsRoot = 'assets/',
  forceRotation = 'landscape',
  components = {},
} = {}) => {
  const conf = {
    gameURL: '',
    gameVersion: '2',
    type,
    parent,
    width,
    height,
    safeWidth,
    safeHeight,
    forceRotation,
    banner: false,
    fps: 60,
    maxParallelDownloads: 32,
    audio: {
      disableWebAudio: false,
    },
    plugins: {
      global: [],
      scene: [
        {
          key: 'SpinePlugin',
          plugin: window.SpinePlugin,
          sceneKey: 'spine',
          mapping: 'spine',
        },
      ],
    },
    scale: {
      mode: Phaser.Scale.HEIGHT_CONTROLS_WIDTH,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    dom: {
      createContainer: true,
    },
    // AppBase components
    components: MergeComponents(
      {
        copy: CopyPlugin,
        settings: SettingsPlugin,
        sceneController: SceneControllerPlugin,
        soundController: SoundPlugin,
        viewportController: ViewportPlugin,
        shortcuts: ShortcutsPlugin,
        loader: LoaderPlugin,
        hud: GameHudPlugin,
        vibrate: VibratePlugin,
        captions: CaptionsPlugin,
        saves: SavesPlugin,
        monitor: MonitorPlugin,
        logger: LoggerPlugin,
        appUrls: { component: AppUrlsPlugin, data: { base: gameURL, assets: assetsRoot } },
      },
      components
    ),
  };
  if (physics) {
    conf.physics = {
      default: 'matter',
      matter: {
        debug: JW_WATCH,
        debugBodyColor: 0x000000,
      },
    };
  }

  const deviceMetric = getDeviceMetric();
  var ua = window.navigator.userAgent;
  var msie = ua.indexOf('MSIE');
  // force canvas for IE
  if (msie > 0 || !!ua.match(/Trident.*rv\:11\./)) {
    conf.type = Phaser.CANVAS;
  }

  // force canvas for all kindles
  if (deviceMetric.platform.kindle) {
    conf.type = Phaser.CANVAS;
  }

  // force canvas for old samsungs
  if (
    !!ua.match(/SM-T550/) ||
    !!ua.match(/SM-T350/) ||
    !!ua.match(/SM-T580/) ||
    !!ua.match(/SamsungBrowser/)
  ) {
    conf.type = Phaser.CANVAS;
  }

  // force canvas for low-end android devices
  if (deviceMetric.platform.os === 'android' && deviceMetric.category === 'LOW_END') {
    conf.type = Phaser.CANVAS;
  }

  // disable aliasing on all lowend devices, kindles, and ios smartphone devices
  const disableAntiAlias =
    deviceMetric.category === 'LOW_END' ||
    deviceMetric.platform.kindle ||
    (deviceMetric.platform.os === 'ios' && deviceMetric.platform.type === 'smartphone');

  conf.render = {
    antialias: disableAntiAlias ? false : true,
    antialiasGL: disableAntiAlias ? false : true,
    batchSize: disableAntiAlias ? 2048 : 4096,
    clearBeforeRender: false,
    transparent: false,
  };

  return conf;
};

export const MergeComponents = (masterComponents, mergeComponents) => {
  const results = Object.keys(masterComponents).reduce((obj, key) => {
    const master = masterComponents[key];
    const merge = mergeComponents[key];
    if (merge) {
      if (merge.prototype instanceof Phaser.Plugins.BasePlugin) {
        obj[key] = merge;
      } else if (master.prototype instanceof Phaser.Plugins.BasePlugin) {
        obj[key] = { component: master, ...merge };
      } else {
        obj[key] = { ...master, ...merge };
      }
    } else {
      obj[key] = master;
    }
    return obj;
  }, {});
  Object.keys(mergeComponents).forEach((key) => {
    if (!results[key]) results[key] = mergeComponents[key];
  });
  return results;
};

/**
 * @typedef GetConfigBaseOptions
 * @property {string} [gameURL='./'] Absolute url to game root
 * @property {string} [assetsRoot='assets/'] Relative path to assets root from gameURL
 * @property {number} [width=1680] Default game width
 * @property {number} [height=720] Default game height
 * @property {number} [safeWidth=960] Default game safe Width
 * @property {number} [safeHeight=720] Default game safe Height
 * @property {Object} [components=null] Component configuration
 * @property {number} [type=Phaser.AUTO] Phaser Render Type
 * @property {string} [forceRotation='landscape'] Force orientation
 * @property {bool} [physics=true] Phaser physics enabled
 */
